import React, { useEffect, useState, useMemo, memo } from "react";
import { catalogByPart, getAllUsedCatalogs } from "../../../../../apis";
import { alpha, Box, Collapse, Grid2, Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { EGCheckbox } from "../../../../MainComponents/Checkbox/EGCheckbox";
import { EGBasicSelect } from "../../../../MainComponents/Select/EGBasicSelect";
import { EGIconButton } from "../../../../MainComponents/Button/IconButton/IconButton";
import { Loading } from "../../../../Loading/String/Loading";

/**
 * Parses a catalog root key and returns structured information.
 * A catalog key has the format “ORIGIN::NAME”, e.g.:
 * - “OWN::RC-001” → user's own catalog.
 * - “EG::FIN-200” → EthicGlobal global catalog
 * @param {string} rootKey - Catalog key in “ORIGIN::CATALOG” format.
 * @returns {{ is_own: boolean, catalogue: string }} Object with:
 * - is_own: true if the catalog is the user's (“OWN”).
 * catalogue: catalog name extracted from the second segment.
 */
const parseRootKey = (rootKey = "") => {
  const parts = rootKey.split("::");
  return {
    is_own: parts[0] === "OWN",
    catalogue: parts[1] || "",
  };
};

/**
 * Converts the first character of a string to uppercase.
 * If the value received is not a string, returns an empty string.
 */
const capitalize = (str) =>
  typeof str === "string" ? str.charAt(0).toUpperCase() + str.slice(1) : "";

/**
 * SelectedGroup
 * Renders an individual catalog section with nested Selects and expand button.
 * Synchronizes local status with the selected path and allows to activate/deactivate groups.
 * Props:
 * - rootKey: string → Catalog root key (e.g., “OWN::RC-123”).
 * - rootData: root catalog object
 * - isEnable: boolean → If the group is active.
 * - onToggleEnabled: function to enable/disable the group
 * - selectedKey: selected relative path (e.g., “x::y::z”)
 * - onChange: callback with selection changes
 */

const SelectedGroup = memo(
  ({ rootKey, rootData, isEnable, onToggleEnabled, selectedKey, onChange }) => {
    const { t, i18n } = useTranslation("report");
    const lang = i18n.language || "es";
    const theme = useTheme();

    const [selectedPath, setSelectedPath] = useState([]);
    const [fetchedLevels, setFetchedLevels] = useState({});
    const [manuallyExpandedLevels, setManuallyExpandedLevels] = useState([]);
    const [pendingOnChangePath, setPendingOnChangePath] = useState(null);

    // Gets and capitalizes the localized tag of an item, based on the current language.
    const getLocalizedLabel = (item) => {
      if (!item) return "";
      const { locales = {}, label } = item;
      if (
        locales &&
        typeof locales === "object" &&
        Object.keys(locales).length > 0
      ) {
        const labelFromLocale = locales[lang] || Object.values(locales)[0];
        return capitalize(labelFromLocale || label || "");
      } else {
        return capitalize(label || "");
      }
    };

    // Gets the localized label of the parent level on a selection path.
    const getParentLabel = (level) => {
      const parentKey = selectedPath[level - 1]?.key;
      if (!parentKey) return "";
      const parentOption = optionsByLevel[level - 1]?.find(
        (item) => item.value === parentKey
      );
      return getLocalizedLabel(parentOption);
    };

    // Constructs a complete selection string from a root key and a selection path.
    const buildSelectionString = (key, path) => {
      const parts = [key];
      path.forEach((entry) => {
        parts.push(entry.key);
      });
      return parts.join("::");
    };

    useEffect(() => {
      if (pendingOnChangePath) {
        // Build the complete string from rootKey and the pending route
        const finalPathString = buildSelectionString(
          rootKey,
          pendingOnChangePath
        ).replace(`${rootKey}::`, ""); // Remove the root prefix (the caller has it).
        // Notifies outward change of selection
        onChange(rootKey, finalPathString);
        // Clears the pending status to avoid resubmits
        setPendingOnChangePath(null);
      }
    }, [pendingOnChangePath]);

    useEffect(() => {
      const rehydrate = async () => {
        // Nothing to rehydrate
        if (!selectedKey) return;
        // Clean the “type::” prefix if it exists and split into keys.
        const clean = selectedKey.replace(/^type::/, "");
        const keys = clean.split("::").filter(Boolean);
        // Avoid redoing everything if the same thing is already selected.
        const current = selectedPath.map((e) => e.key);
        if (
          keys.length === current.length &&
          keys.every((k, i) => k === current[i])
        )
          return;

        const newPath = [];
        const newFetchedLevels = {};
        const { is_own, catalogue } = parseRootKey(rootKey);
        // Iterate over each key and ask for data of the corresponding level.
        for (let i = 0; i < keys.length; i++) {
          const p = newPath
            .map((e) => `type::${e.key}`)
            .concat(`type::${keys[i]}`)
            .join("::");

          const resp = await catalogByPart({ is_own, catalogue, path: p });
          // if there are children at this level, he/she keeps them
          newPath.push({ key: keys[i] });
          if (resp.data?.length) {
            newFetchedLevels[i + 1] = resp.data;
          }
        }
        // Update the statuses
        setSelectedPath(newPath);
        setFetchedLevels(newFetchedLevels);
        setManuallyExpandedLevels(keys.map((_, i) => i));
      };

      rehydrate();
    }, [selectedKey]);
    // Acts as a resetter of the internal state of the component when no key is selected
    useEffect(() => {
      if (!selectedKey) {
        setSelectedPath([]);
        setFetchedLevels({});
        setManuallyExpandedLevels([]);
        setPendingOnChangePath(null);
      }
    }, [selectedKey]);

    // It generates and stores an object where each key represents a hierarchical level of the catalog.
    const optionsByLevel = useMemo(() => {
      const result = {};

      if (rootData?.type) {
        result[0] = Object.entries(rootData.type).map(([key, obj]) => ({
          label: getLocalizedLabel(obj),
          value: key,
        }));
      }

      Object.entries(fetchedLevels).forEach(([level, items]) => {
        result[level] = items.map((item) => ({
          label: getLocalizedLabel(item),
          value: item.key,
        }));
      });

      return result;
    }, [rootData, fetchedLevels, lang]);

    // Handles the selection of an option at a hierarchical level, updating the selected path, dynamically loading sublevels if they exist, clearing lower levels and notifying the parent component of the change via onChange.
    const handleChange = async (level, selectedKey) => {
      try {
        const { is_own, catalogue } = parseRootKey(rootKey);
        const newSelectPath = [
          ...selectedPath.slice(0, level),
          { key: selectedKey },
        ];

        const newPath = newSelectPath
          .map((entry) => `type::${entry.key}`)
          .join("::");

        const resp = await catalogByPart({ is_own, catalogue, path: newPath });

        setSelectedPath(newSelectPath);

        if (resp.data?.length) {
          setFetchedLevels((prev) => ({
            ...Object.fromEntries(
              Object.entries(prev).filter(([lvl]) => lvl <= level)
            ),
            [level + 1]: resp.data,
          }));
        } else {
          setFetchedLevels((prev) =>
            Object.fromEntries(
              Object.entries(prev).filter(([lvl]) => lvl <= level)
            )
          );
        }

        setManuallyExpandedLevels((prev) => prev.filter((lvl) => lvl <= level));

        const relativePath = buildSelectionString(
          rootKey,
          newSelectPath
        ).replace(`${rootKey}::`, "");
        onChange(rootKey, relativePath);
      } catch (error) {
        console.error("Error fetching catalog level:", error);
      }
    };

    // Toggles the next level expansion (level + 1) in the hierarchical selector, updating the manual expansion status, clearing routes if contracted, and preparing the route to notify changes if necessary.
    const toggleExpandLevel = (level) => {
      const nextLevel = level + 1;
      const isExpanded = manuallyExpandedLevels.includes(nextLevel);

      if (isExpanded) {
        setManuallyExpandedLevels((prev) =>
          prev.filter((lvl) => lvl !== nextLevel)
        );
        const newPath = selectedPath.slice(0, nextLevel);
        setSelectedPath(newPath);
        setPendingOnChangePath(newPath);
      } else {
        setManuallyExpandedLevels((prev) => [...prev, nextLevel]);
      }
    };
    // Generate a series of selection components (EGBasicSelect) that represent different levels of a selection hierarchy
    //  The function also handles the expansion and contraction of levels, displaying expansion buttons (EGIconButton) when sublevels are available for each selected level.
    const renderSelects = () => {
      const selects = [];

      for (let level = 0; level <= selectedPath.length; level++) {
        const options = optionsByLevel[level] || [];
        if (!options.length) break;

        const selectedKey = selectedPath[level]?.key || "";
        const hasChildren = fetchedLevels[level + 1]?.length > 0;
        const isExpanded = manuallyExpandedLevels.includes(level + 1);

        selects.push(
          <Stack
            key={`select-level-${level}`}
            direction="row"
            flexWrap="nowrap"
            alignItems="center"
            spacing={0.6}
            pb={1.25}
            pt={level === 0 ? 1 : 0}
          >
            <EGBasicSelect
              label={`${
                level === 0
                  ? getLocalizedLabel(rootData)
                  : getParentLabel(level)
              }:`}
              value={selectedKey || ""}
              onChange={(e) => handleChange(level, e.target.value)}
              options={options}
            />

            {selectedKey && hasChildren && (
              <EGIconButton
                onClick={() => toggleExpandLevel(level)}
                iconColor={isExpanded ? "error" : "success"}
                colorHover={isExpanded ? "redV" : "greenV"}
                iconName={isExpanded ? "minus_square" : "add_square"}
                title={
                  isExpanded
                    ? t("Filter.remove_subcategory")
                    : t("Filter.add_subcategory")
                }
              />
            )}
          </Stack>
        );

        if (!selectedKey || !manuallyExpandedLevels.includes(level + 1)) break;
      }

      return selects;
    };

    return (
      <Stack
        spacing={0.25}
        sx={{
          opacity: isEnable ? 1 : 0.8,
          mt: 0.3,
          px: 0.5,
          border: isEnable && `${theme.palette.blueGray50.main} 1px solid`,
          borderRadius: 2,
          bgcolor: isEnable && alpha(theme.palette.blueGray50.main, 0.1),
        }}
      >
        <EGCheckbox
          checked={isEnable}
          onChange={() => onToggleEnabled(rootKey)}
          color="primary"
          label={getLocalizedLabel(rootData)}
        />

        <Collapse in={isEnable} timeout={250} className="rounded">
          {renderSelects()}
        </Collapse>
      </Stack>
    );
  }
);

/**
 * AdvancedSearch component
 *
 * Allows to apply hierarchical filters on data catalogs.
 * Each catalog group can contain multiple levels (nested Selects),
 * which are manually enabled and synchronized with `catalogSearch`.
 *
 */

export const AdvancedSearch = ({
  catalogSearch,
  setCatalogSearch,
  isResettingCatalog,
}) => {
  const { t } = useTranslation("report");

  const [catalogs, setCatalogs] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Initial States
  const [enabledGroups, setEnabledGroups] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Rehydration of the data when mounting the component
  useEffect(() => {
    setIsLoading(true);
    getAllUsedCatalogs()
      .then((res) => {
        // Filter only catalogs with valid type
        const validCatalogs = Object.fromEntries(
          Object.entries(res || {}).filter(
            ([_, value]) => value?.type && Object.keys(value.type).length > 0
          )
        );
        setCatalogs(validCatalogs);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setIsLoading(false);
      });
  }, []);

  const parseCatalogEntry = (entry) => {
    const parts = entry.split("::");
    // The first two parts are the rootKey
    const key = parts.slice(0, 2).join("::"); // e.g. "EG::RC-100"
    // The rest is the relative path
    const rest = parts.slice(2).join("::"); // e.g. "grievance::financialInfo"
    return {
      key,
      selected: rest || null,
    };
  };

  // State rehydration from catalogSearch
  useEffect(() => {
    if (
      !hasInitialized &&
      !isLoading &&
      Object.keys(catalogs).length > 0 &&
      catalogSearch?.length > 0
    ) {
      const newEnabledGroups = {};
      const newSelectedValues = {};

      catalogSearch.forEach((entry) => {
        const { key, selected } = parseCatalogEntry(entry);

        if (catalogs[key]) {
          newEnabledGroups[key] = true;
          if (selected) {
            newSelectedValues[key] = selected;
          }
        } else {
          console.warn(`⚠️ Catalog not found for key: ${key}`);
        }
      });

      const total = Object.keys(catalogs).length;
      const enabled = Object.keys(newEnabledGroups).length;
      const allEnabled = total > 0 && enabled === total;

      setEnabledGroups(newEnabledGroups);
      setSelectedValues(newSelectedValues);
      setSelectAll(allEnabled);
      setHasInitialized(true);
    }
  }, [isLoading, catalogs, catalogSearch, hasInitialized]);

  //  Reset filters (use of resetTrigger)
  useEffect(() => {
    if (catalogSearch?.length === 0 && !isLoading) {
      setEnabledGroups({});
      setSelectedValues({});
      setSelectAll(false);
      setHasInitialized(false);
    }
  }, [catalogSearch, isLoading]);

  const areArraysEqual = (a, b) => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((val, index) => val === sortedB[index]);
  };

  useEffect(() => {
    if (!hasInitialized || isLoading) return;

    const selectedKeys = Object.entries(enabledGroups)
      .filter(([_, isEnabled]) => isEnabled)
      .map(([key]) =>
        selectedValues[key] !== undefined
          ? `${key}::${selectedValues[key]}`
          : null
      )
      .filter(Boolean);

    if (isResettingCatalog) return;

    if (!areArraysEqual(selectedKeys, catalogSearch)) {
      setCatalogSearch(selectedKeys);
    }
  }, [enabledGroups, selectedValues, catalogSearch, hasInitialized, isLoading]);

  // State change handlers
  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);

    const updatedGroups = Object.keys(catalogs).reduce((acc, key) => {
      acc[key] = checked;
      return acc;
    }, {});

    setEnabledGroups(updatedGroups);
    if (!checked) {
      setSelectedValues({});
      setCatalogSearch([]);
    }
  };

  // toggleGroup function is designed to toggle (activate/deactivate) a group within a set of options.
  const toggleGroup = (key) => {
    setEnabledGroups((prev) => {
      const newState = { ...prev, [key]: !prev[key] };
      const allEnabled =
        Object.keys(catalogs).length > 0 &&
        Object.keys(catalogs).every((k) => newState[k]);
      setSelectAll(allEnabled);
      return newState;
    });
    // If a group is deactivated, we also clear its selection
    setSelectedValues((prevSelected) => {
      const updated = { ...prevSelected };
      delete updated[key];
      return updated;
    });
  };

  const handleSelectChange = (key, relativePath) => {
    setHasInitialized(true);
    setSelectedValues((prev) => ({
      ...prev,
      [key]: relativePath,
    }));
  };

  return (
    <Grid2 container sx={{ width: "100%", mt: 1, px: 0.5 }} size={12}>
      {isLoading ? (
        <Grid2
          size={12}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "25vh",
            flexFlow: "column nowrap",
          }}
        >
          <Loading withoutLogo={false} />
        </Grid2>
      ) : (
        <>
          <Grid2 size={12} sx={{ opacity: selectAll ? 1 : 0.8 }}>
            <Box sx={{ display: "inline-flex", maxWidth: "fit-content" }}>
              <EGCheckbox
                size="small"
                color="primary"
                name="selectAll"
                id="selectAll"
                checked={selectAll}
                onChange={handleSelectAll}
                sx={{ maxWidth: "auto" }}
                label={
                  selectAll ? t("Filter.deselect_all") : t("Filter.select_all")
                }
              />
            </Box>
          </Grid2>

          {Object.entries(catalogs).map(([key, value]) => (
            <Grid2 key={key} size={{ xs: 12, sm: 6, lg: 4, xl: 3 }} px={0.75}>
              <SelectedGroup
                rootKey={key}
                rootData={value}
                isEnable={enabledGroups[key] || false}
                onToggleEnabled={toggleGroup}
                selectedKey={selectedValues[key]}
                onChange={handleSelectChange}
              />
            </Grid2>
          ))}
        </>
      )}
    </Grid2>
  );
};
